import { getErrorMessage } from '@/utils/getErrorMessage';
import { API, BlockAPI, BlockTool } from '@editorjs/editorjs';
import ButtonDomFactory from '../common/factory/button-dom-factory';
import TextAreaDomFactory from '../common/factory/text-area-factory';
import ImageViewBuilder from './builder/image-view-builder';
import TextAreaBuilder from './builder/text-area-builder';
import ImageViewFactory from './facades/image-view-factory';
import './index.css';

export interface ImageGeneratorBlockConfig {
  label: string;
  callbacks: {
    onGenerateImage?: (prompt: string) => Promise<string>;
  };
}

interface ImageGeneratorBlockData {
  prompt: string;
  image: string;
}

type Constructor = {
  api: API;
  data: ImageGeneratorBlockData;
  config?: ImageGeneratorBlockConfig;
  block: BlockAPI;
};

export default class ImageGeneratorBlockTool implements BlockTool {
  private readonly api: API;
  private readonly data: ImageGeneratorBlockData;
  private readonly config: ImageGeneratorBlockConfig | undefined;
  private readonly block: BlockAPI;
  private wrapper: HTMLElement | undefined;
  private textAreaBuilder: TextAreaBuilder;
  private imageViewBuilder: ImageViewBuilder;
  public isLoading: boolean = false;
  public error: string | undefined;
  public lastSuccessPrompt: string | undefined;

  static get toolbox() {
    return {
      title: 'Gerador de imagens',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>`,
    };
  }

  constructor({ api, data, config, block }: Constructor) {
    this.api = api;
    this.data = data;
    this.config = config;
    this.block = block;
    this.wrapper = undefined;
    this.lastSuccessPrompt = data.prompt;
    this.textAreaBuilder = new TextAreaBuilder(
      new TextAreaDomFactory(this.api.i18n),
      new ButtonDomFactory(this.api.i18n),
    );
    this.imageViewBuilder = new ImageViewBuilder(
      new ImageViewFactory(this.api.i18n),
    );
  }

  public getData() {
    return this.data;
  }

  public generateImage = async () => {
    this.isLoading = true;
    this.error = undefined;
    this.rerender();
    try {
      const image = await this.config?.callbacks.onGenerateImage?.(
        this.data.prompt,
      );
      this.data.image = image || '';
      this.lastSuccessPrompt = this.data.prompt;
    } catch (error) {
      this.error = getErrorMessage(error);
      this.data.prompt = this.lastSuccessPrompt || '';
    }
    this.isLoading = false;
    this.rerender();
  };

  updatePrompt = (prompt: string) => {
    this.data.prompt = prompt;
    this.block.save();
  };

  imageDirector = () => {
    if (this.isLoading) return this.imageViewBuilder.withLoading().build();
    if (!this.data.image) return this.imageViewBuilder.withFallback().build();
    return this.imageViewBuilder.withImage({ src: this.data.image }).build();
  };

  public async renderImageGenerator() {
    const container = document.createElement('div');
    container.classList.add('image-generator-container');
    const input = this.textAreaBuilder
      .withLabel(this.config?.label)
      .withArea({ value: this.data.prompt })
      .withOnChange(this.updatePrompt)
      .withError(this.error)
      .withButton({
        text: this.api.i18n.t('Generate image'),
        onClick: this.generateImage,
        disabled: this.isLoading,
      })
      .build();
    const imageView = this.imageDirector();
    container.append(input, imageView);
    return container;
  }

  private clearWrapper() {
    if (this.wrapper) {
      this.wrapper.innerHTML = '';
      this.textAreaBuilder.cleanup();
      this.imageViewBuilder.cleanup();
    }
  }

  private rerender() {
    this.clearWrapper();
    this.mount();
  }

  private async mount() {
    const imagegenerator = await this.renderImageGenerator();
    this.wrapper?.appendChild(imagegenerator);
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('image-generator-tool');
    this.mount();
    return this.wrapper;
  }

  save() {
    return this.data;
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get pasteConfig() {
    return {
      tags: [],
    };
  }

  static get sanitize() {
    return {};
  }
}
